import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Select,
  Spinner,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { PAGE_PADDING, FORM_PADDING_WIDTH } from "../../helpers/constant";
import CustomButton from "../../components/UI/CustomButton";
import { useEffect, useMemo, useState } from "react";
import { useFormTheme } from "../../hooks/useFormTheme";
import { validateEmail } from "../../helpers/utils";
import CustomInput from "../../components/UI/CustomInput";
import { useAuth } from "../../context/AuthContext";
import FormErrorBox from "../../components/UI/FormErrorBox";
import { useTranslation } from "react-i18next";

const cities = [
  { value: "douala", label: "Douala" },
  { value: "yaounde", label: "Yaounde" },
  { value: "bafoussam", label: "Bafoussam" },
];

const EditProfilePage = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const {
    appUserProfile: user,
    updateAttributesHandler,
    isLoading,
  } = useAuth();

  const parsedUser = useMemo(() => {
    return {
      firstName: user?.firstname ?? "",
      lastName: user?.lastname ?? "",
      email: user?.email ?? "",
      city: (user?.city ?? "").toLowerCase(),
      phone: user?.phoneNumber ?? "",
    };
  }, [user]);

  const [form, setForm] = useState(parsedUser);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const { errorColor } = useFormTheme();

  // useEffect(() => {
  //   refreshUserAttributes().catch(() => {
  //     toast({
  //       title: "Unable to load profile",
  //       description: "Please try refreshing the page.",
  //       status: "error",
  //       duration: 4000,
  //       isClosable: true,
  //       position: "top",
  //     });
  //   });
  // }, [refreshUserAttributes, toast]);

  useEffect(() => {
    setForm(parsedUser);
  }, [parsedUser]);

  // Validation logic (simplified from Signup)
  const validate = (field?: string, value?: string) => {
    const newErrors: Record<string, string> =
      field && hasSubmitted ? { ...errors } : {};
    const f = {
      ...form,
      ...(field && field in form
        ? { [field]: value ?? form[field as keyof typeof form] }
        : {}),
    };

    const check = (
      name: keyof typeof form,
      condition: boolean,
      msg: string,
    ) => {
      if (!field || field === name) {
        condition ? (newErrors[name] = msg) : delete newErrors[name];
      }
    };

    check(
      "firstName",
      !f.firstName,
      String(t("auth.common.firstNameRequired")),
    );
    check("lastName", !f.lastName, String(t("auth.common.lastNameRequired")));
    check(
      "email",
      !validateEmail(f.email),
      String(t("auth.common.invalidEmail")),
    );
    check("city", !f.city, String(t("auth.common.cityRequired")));

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (!validate()) return;

    setLoading(true);
    try {
      await updateAttributesHandler({
        given_name: form.firstName,
        family_name: form.lastName,
        name: `${form.firstName} ${form.lastName}`.trim(),
        email: form.email,
        zoneinfo: form.city,
      });

      toast({
        title: String(t("auth.editProfile.successTitle")),
        description: String(t("auth.editProfile.successDescription")),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: String(t("auth.editProfile.errorTitle")),
        description:
          error instanceof Error ? error.message : String(t("common.tryAgain")),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <VStack
      w={{ base: "full", md: "70%", lg: "400px", "2xl": "425px" }}
      px={PAGE_PADDING}
      mx="auto"
      align="stretch"
      pt={{ base: 0, lg: "8px" }}
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing="24px" align="stretch" w={FORM_PADDING_WIDTH}>
          {["firstName", "lastName", "email", "phone"].map((field) => (
            <FormControl key={field} isInvalid={!!errors[field]}>
              <FormLabel textTransform="capitalize">
                {field === "firstName"
                  ? t("auth.editProfile.firstName")
                  : field === "lastName"
                    ? t("auth.editProfile.lastName")
                    : field === "email"
                      ? t("auth.editProfile.email")
                      : t("auth.editProfile.phone")}
              </FormLabel>

              <CustomInput
                type={
                  field === "email"
                    ? "email"
                    : field === "phone"
                      ? "tel"
                      : "text"
                }
                placeholder={
                  field === "firstName"
                    ? String(t("auth.editProfile.firstNamePlaceholder"))
                    : field === "lastName"
                      ? String(t("auth.editProfile.lastNamePlaceholder"))
                      : field === "email"
                        ? String(t("auth.editProfile.emailPlaceholder"))
                        : String(t("auth.editProfile.phonePlaceholder"))
                }
                value={(form as any)[field]}
                error={!!errors[field]}
                isDisabled={field === "email" || field === "phone"}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm({ ...form, [field]: value });
                  if (hasSubmitted) {
                    validate(field, value);
                  }
                }}
              />

              <FormErrorMessage color={errorColor}>
                {errors[field]}
              </FormErrorMessage>
            </FormControl>
          ))}

          {/* City dropdown */}
          <FormControl isInvalid={!!errors.city}>
            <FormLabel>{t("auth.editProfile.city")}</FormLabel>
            <Select
              placeholder={String(t("auth.editProfile.cityPlaceholder"))}
              value={form.city}
              isDisabled
              onChange={(e) => {
                const val = e.target.value;
                setForm({ ...form, city: val });
                if (hasSubmitted) {
                  validate("city", val);
                }
              }}
            >
              {cities.map((city) => (
                <option key={city.value} value={city.value}>
                  {city.label}
                </option>
              ))}
            </Select>

            <FormErrorMessage color={errorColor}>
              {errors.city}
            </FormErrorMessage>
          </FormControl>

          <FormErrorBox errors={Object.values(errors)} />

          <CustomButton type="submit" isDisabled={loading || isLoading}>
            {loading || isLoading ? (
              <Spinner size="sm" />
            ) : (
              t("auth.editProfile.submit")
            )}
          </CustomButton>
        </VStack>
      </form>
    </VStack>
  );
};

export default EditProfilePage;
